import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ApiError, PackageRow, PerformanceRow, TicketTypeRow } from '../api';
import { eventEndpoints, packageEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT, dollars, humanize, toCents } from './event/shared';
import { InstallmentTermsPanel } from './InstallmentPanels';
import { WEEKDAYS, seasonEndpoints } from '../api-seasons';
import type { SeasonComponentsInput, SeasonComponentsPlan } from '../api-seasons';
import { dateWords } from '../terms';

/**
 * Packages (Phase 12, D-63): fixed packages, choose-your-own, season
 * tickets, table subscriptions and membership passes. A product is built
 * here — its kind, its components (a performance and ticket type each),
 * its price per channel — then put on sale; holders, renewals offered and
 * lapsed show on the product.
 */

const KINDS = [
  ['FIXED_PACKAGE', 'Fixed package', `Every listed ${dateWords().one}, priced together`],
  ['CHOOSE_YOUR_OWN', 'Choose-your-own', `The customer picks between min and max of the optional ${dateWords().many}`],
  ['SEASON_TICKET', 'Season ticket', `The same seat on every ${dateWords().one}; renewable next season`],
  ['TABLE_SUBSCRIPTION', 'Table subscription', `A whole table, the same one on every ${dateWords().one}; renewable`],
  ['MEMBERSHIP_PASS', 'Membership pass', 'Redeemed into tickets for the listed events, within its limits'],
] as const;

const toIso = (local: string) => (local ? new Date(local).toISOString().replace(/\.\d{3}Z$/, '.000Z') : null);
const toLocal = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(0, 16) : '');

export default function PackagesPage() {
  const { id } = useParams();
  const can = usePermissions();
  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  if (id) return <PackageDetail id={id} />;
  return <PackageList />;
}

function PackageList() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const packages = useQuery({ queryKey: ['packages'], queryFn: () => packageEndpoints.list() });
  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {creating ? (
        <ProductForm
          product={null}
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['packages'] });
          }}
          onCancel={() => setCreating(false)}
          onError={setError}
        />
      ) : null}
      <Panel title="Packages" description="Season tickets, subscriptions, fixed and choose-your-own packages, and passes. A package goes on sale once it has components and a price." actions={can('packages.manage') && !creating ? <Button variant="primary" onClick={() => setCreating(true)}>New package</Button> : null}>
        <Table headers={['Package', 'Kind', 'Components', 'Price', 'Holders', 'Status']}>
          {(packages.data?.packages ?? []).length === 0 ? (
            <EmptyRow columns={6}>No packages yet.</EmptyRow>
          ) : (
            packages.data!.packages.map((p) => (
              <tr key={p.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <Link to={`/packages/${p.id}`} className="font-medium text-ink-900 hover:underline">
                    {p.name}
                  </Link>
                  <div className="font-mono text-xs text-ink-400">{p.code}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{humanize(p.package_kind)}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{p.components.length}</td>
                <td className="tabular px-5 py-3 text-sm">{p.prices.map((x) => `${x.channel ?? 'all'} ${dollars(x.price_cents)}`).join(' · ') || '—'}</td>
                <td className="tabular px-5 py-3 text-sm">{p.holders.active}</td>
                <td className="px-5 py-3">
                  <Badge tone={p.status === 'ON_SALE' ? 'ok' : p.status === 'DRAFT' ? 'neutral' : 'warn'}>{humanize(p.status)}</Badge>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function ProductForm({ product, onDone, onCancel, onError }: { product: PackageRow | null; onDone: () => void; onCancel: () => void; onError: (message: string) => void }) {
  const seasons = useQuery({ queryKey: ['seasons', 'packages'], queryFn: packageEndpoints.seasons });
  const seriesList = useQuery({ queryKey: ['collections', 'series'], queryFn: () => eventEndpoints.series() });
  const all = useQuery({ queryKey: ['packages'], queryFn: () => packageEndpoints.list() });
  const [form, setForm] = useState({
    code: product?.code ?? '',
    name: product?.name ?? '',
    description: product?.description ?? '',
    package_kind: product?.package_kind ?? 'FIXED_PACKAGE',
    season_id: product?.season_id ?? '',
    series_id: product?.series_id ?? '',
    allocation_method: product?.allocation_method ?? 'PROPORTIONAL_TO_FACE_VALUE',
    min_selections: product?.min_selections?.toString() ?? '',
    max_selections: product?.max_selections?.toString() ?? '',
    renews_package_product_id: product?.renews_package_product_id ?? '',
    pass_valid_from: toLocal(product?.pass_valid_from ?? null),
    pass_valid_to: toLocal(product?.pass_valid_to ?? null),
    pass_admissions_per_performance: String(product?.pass_admissions_per_performance ?? 1),
    pass_total_redemptions: product?.pass_total_redemptions?.toString() ?? '',
    pass_covers_season: product?.pass_covers_season === 1,
    pass_ticket_type_code: product?.pass_ticket_type_code ?? '',
    max_per_order: String(product?.max_per_order ?? 8),
  });
  const [newSeason, setNewSeason] = useState({ code: '', name: '', starts_on: '', ends_on: '' });
  const queryClient = useQueryClient();
  const createSeason = useMutation({
    mutationFn: () => packageEndpoints.createSeason(newSeason),
    onSuccess: (r) => {
      setForm({ ...form, season_id: r.season.id });
      setNewSeason({ code: '', name: '', starts_on: '', ends_on: '' });
      void queryClient.invalidateQueries({ queryKey: ['seasons'] });
    },
    onError: (cause: ApiError) => onError(cause.message),
  });
  const save = useMutation({
    mutationFn: () => {
      const isPass = form.package_kind === 'MEMBERSHIP_PASS';
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        package_kind: form.package_kind,
        season_id: form.season_id || null,
        series_id: form.series_id || null,
        allocation_method: form.allocation_method,
        min_selections: form.package_kind === 'CHOOSE_YOUR_OWN' && form.min_selections ? Number(form.min_selections) : null,
        max_selections: form.package_kind === 'CHOOSE_YOUR_OWN' && form.max_selections ? Number(form.max_selections) : null,
        renews_package_product_id: form.renews_package_product_id || null,
        pass_valid_from: isPass ? toIso(form.pass_valid_from) : null,
        pass_valid_to: isPass ? toIso(form.pass_valid_to) : null,
        pass_admissions_per_performance: isPass ? Number(form.pass_admissions_per_performance) || 1 : 1,
        pass_total_redemptions: isPass && form.pass_total_redemptions ? Number(form.pass_total_redemptions) : null,
        // Every regular-season game of the season, not only the components (D-373).
        pass_covers_season: isPass && form.pass_covers_season,
        pass_ticket_type_code: isPass && form.pass_covers_season ? form.pass_ticket_type_code.trim().toUpperCase() || null : null,
        max_per_order: Number(form.max_per_order) || 8,
      };
      return product ? packageEndpoints.update(product.id, body) : packageEndpoints.create(body);
    },
    onSuccess: onDone,
    onError: (cause: ApiError) => onError(cause.message),
  });
  const kind = KINDS.find((k) => k[0] === form.package_kind)!;
  const heldByAnyone = Boolean(product) && (product!.holders.active + product!.holders.pending) > 0;
  const renewable = form.package_kind === 'SEASON_TICKET' || form.package_kind === 'TABLE_SUBSCRIPTION';
  return (
    <Panel title={product ? `Edit ${product.name}` : 'New package'} description={kind[2]}>
      <form
        className="grid gap-3 px-5 py-4 md:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Code" htmlFor="pk-code" hint="Short and unique; it is the storefront URL">
          <input id="pk-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={INPUT} required disabled={Boolean(product)} />
        </Field>
        <Field label="Name" htmlFor="pk-name">
          <input id="pk-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT} required />
        </Field>
        <Field label="Kind" htmlFor="pk-kind">
          {/* Editable while nobody holds it (D-235): the kind decides what a
              holder owns, so it is locked by holders rather than by age. */}
          <select id="pk-kind" value={form.package_kind} onChange={(e) => setForm({ ...form, package_kind: e.target.value as (typeof KINDS)[number][0] })} className={INPUT} disabled={heldByAnyone}>
            {KINDS.map((k) => (
              <option key={k[0]} value={k[0]}>
                {k[1]}
              </option>
            ))}
          </select>
          {heldByAnyone ? <p className="mt-1 text-xs text-ink-500">Locked: somebody holds this package, and its kind decides what they own.</p> : null}
        </Field>
        {/* Which series page leads with this package (D-324). */}
        <Field label="Series" htmlFor="pk-series" hint="Optional; the package is offered at the top of the series page, and on its events">
          <select id="pk-series" value={form.series_id} onChange={(e) => setForm({ ...form, series_id: e.target.value })} className={INPUT}>
            <option value="">—</option>
            {(seriesList.data?.series ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Season" htmlFor="pk-season" hint="Optional; groups packages and events">
          <select id="pk-season" value={form.season_id} onChange={(e) => setForm({ ...form, season_id: e.target.value })} className={INPUT}>
            <option value="">—</option>
            {(seasons.data?.seasons ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.starts_on} → {s.ends_on})
              </option>
            ))}
          </select>
          <details className="mt-1 text-xs">
            <summary className="cursor-pointer text-ink-400">New season</summary>
            <div className="mt-1 flex flex-wrap gap-1">
              <input value={newSeason.code} onChange={(e) => setNewSeason({ ...newSeason, code: e.target.value.toUpperCase() })} placeholder="Code" className={`${INPUT} w-20`} aria-label="Season code" />
              <input value={newSeason.name} onChange={(e) => setNewSeason({ ...newSeason, name: e.target.value })} placeholder="Name" className={`${INPUT} w-36`} aria-label="Season name" />
              <input type="date" value={newSeason.starts_on} onChange={(e) => setNewSeason({ ...newSeason, starts_on: e.target.value })} className={INPUT} aria-label="Starts" />
              <input type="date" value={newSeason.ends_on} onChange={(e) => setNewSeason({ ...newSeason, ends_on: e.target.value })} className={INPUT} aria-label="Ends" />
              <Button type="button" disabled={!newSeason.code || !newSeason.name || !newSeason.starts_on || !newSeason.ends_on || createSeason.isPending} onClick={() => createSeason.mutate()}>
                Add
              </Button>
            </div>
          </details>
        </Field>
        <div className="md:col-span-2">
          <Field label="Description" htmlFor="pk-desc" hint="Shown on the storefront">
            <textarea id="pk-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${INPUT} w-full`} rows={3} />
          </Field>
        </div>
        {form.package_kind !== 'MEMBERSHIP_PASS' ? (
          <Field label="Price allocation" htmlFor="pk-alloc" hint="How the package price is spread across the seats for reporting">
            <select id="pk-alloc" value={form.allocation_method} onChange={(e) => setForm({ ...form, allocation_method: e.target.value as PackageRow['allocation_method'] })} className={INPUT}>
              <option value="PROPORTIONAL_TO_FACE_VALUE">Proportional to face value</option>
              <option value="EQUAL_SPLIT">Equal split</option>
              <option value="FIXED_AMOUNTS">Fixed amounts per component</option>
            </select>
          </Field>
        ) : null}
        <Field label="Max per order" htmlFor="pk-max">
          <input id="pk-max" type="number" min={1} max={50} value={form.max_per_order} onChange={(e) => setForm({ ...form, max_per_order: e.target.value })} className={INPUT} />
        </Field>
        {form.package_kind === 'CHOOSE_YOUR_OWN' ? (
          <>
            <Field label="Minimum picks" htmlFor="pk-min">
              <input id="pk-min" type="number" min={1} value={form.min_selections} onChange={(e) => setForm({ ...form, min_selections: e.target.value })} className={INPUT} />
            </Field>
            <Field label="Maximum picks" htmlFor="pk-maxsel">
              <input id="pk-maxsel" type="number" min={1} value={form.max_selections} onChange={(e) => setForm({ ...form, max_selections: e.target.value })} className={INPUT} />
            </Field>
          </>
        ) : null}
        {renewable ? (
          <Field label="Renews" htmlFor="pk-renews" hint="Last season's package whose subscribers get first refusal on their seats">
            <select id="pk-renews" value={form.renews_package_product_id} onChange={(e) => setForm({ ...form, renews_package_product_id: e.target.value })} className={INPUT}>
              <option value="">—</option>
              {(all.data?.packages ?? []).filter((p) => p.id !== product?.id && (p.package_kind === 'SEASON_TICKET' || p.package_kind === 'TABLE_SUBSCRIPTION')).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {form.package_kind === 'MEMBERSHIP_PASS' ? (
          <>
            <Field label="Valid from" htmlFor="pk-from">
              <input id="pk-from" type="datetime-local" value={form.pass_valid_from} onChange={(e) => setForm({ ...form, pass_valid_from: e.target.value })} className={INPUT} />
            </Field>
            <Field label="Valid to" htmlFor="pk-to">
              <input id="pk-to" type="datetime-local" value={form.pass_valid_to} onChange={(e) => setForm({ ...form, pass_valid_to: e.target.value })} className={INPUT} />
            </Field>
            <Field label={`Admissions per ${dateWords().one}`} htmlFor="pk-adm">
              <input id="pk-adm" type="number" min={1} max={50} value={form.pass_admissions_per_performance} onChange={(e) => setForm({ ...form, pass_admissions_per_performance: e.target.value })} className={INPUT} />
            </Field>
            <Field label="Total redemptions" htmlFor="pk-tot" hint="Blank for unlimited">
              <input id="pk-tot" type="number" min={1} value={form.pass_total_redemptions} onChange={(e) => setForm({ ...form, pass_total_redemptions: e.target.value })} className={INPUT} />
            </Field>
            <label className="flex items-center gap-2 text-sm text-ink-900">
              <input type="checkbox" checked={form.pass_covers_season} onChange={(e) => setForm({ ...form, pass_covers_season: e.target.checked })} disabled={!form.season_id} />
              Covers every regular-season game of the season (not playoff games)
            </label>
            {form.pass_covers_season ? (
              <Field label="Ticket type code" htmlFor="pk-pass-type" hint="Each game's own type with this code is used">
                <input id="pk-pass-type" value={form.pass_ticket_type_code} onChange={(e) => setForm({ ...form, pass_ticket_type_code: e.target.value })} placeholder="ADULT" className={INPUT} />
              </Field>
            ) : null}
          </>
        ) : null}
        <div className="flex gap-2 md:col-span-2">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {product ? 'Save' : 'Create'}
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function PackageDetail({ id }: { id: string }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const detail = useQuery({ queryKey: ['packages', id], queryFn: () => packageEndpoints.detail(id) });
  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['packages'] });
  };
  const status = useMutation({ mutationFn: (next: string) => packageEndpoints.setStatus(id, next), onSuccess: refresh, onError: (cause: ApiError) => setError(cause.message) });
  const offer = useMutation({
    mutationFn: (expiresAt: string) => packageEndpoints.offerRenewals(id, expiresAt),
    onSuccess: (r) => {
      setNotice(`Offered ${r.outcome.offered} renewal${r.outcome.offered === 1 ? '' : 's'} (${r.outcome.held_units} seats held)${r.outcome.skipped.length ? `; skipped ${r.outcome.skipped.length}: ${r.outcome.skipped.map((s) => s.reason).join('; ')}` : ''}.`);
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const lapse = useMutation({
    mutationFn: () => packageEndpoints.lapseRenewals(id),
    onSuccess: (r) => {
      setNotice(`Released ${r.outcome.released_units} held seat${r.outcome.released_units === 1 ? '' : 's'}; ${r.outcome.lapsed_instances} subscription${r.outcome.lapsed_instances === 1 ? '' : 's'} lapsed.`);
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  if (detail.error) return <ErrorNote>{(detail.error as ApiError).message}</ErrorNote>;
  if (!detail.data) return <p className="text-sm text-ink-400">Loading…</p>;
  const p = detail.data.package;
  const manage = can('packages.manage');
  const renewable = p.package_kind === 'SEASON_TICKET' || p.package_kind === 'TABLE_SUBSCRIPTION';
  return (
    <div className="space-y-4">
      <Link to="/packages" className="text-sm text-ink-600 hover:underline">
        ← Packages
      </Link>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      {editing ? (
        <ProductForm
          product={p}
          onDone={() => {
            setEditing(false);
            refresh();
          }}
          onCancel={() => setEditing(false)}
          onError={setError}
        />
      ) : (
        <Panel
          title={p.name}
          description={`${humanize(p.package_kind)} · ${p.code}${p.season ? ` · ${p.season.name}` : ''}${p.previous ? ` · renews ${p.previous.name}` : ''}${p.areas.length > 0 ? ` · ${p.areas.map((area) => area.name).join(', ')} only` : ''}`}
          actions={
            <div className="flex flex-wrap gap-2">
              <Badge tone={p.status === 'ON_SALE' ? 'ok' : p.status === 'DRAFT' ? 'neutral' : 'warn'}>{humanize(p.status)}</Badge>
              {manage ? <Button onClick={() => setEditing(true)}>Edit</Button> : null}
              {manage && p.status !== 'ON_SALE' ? (
                <Button variant="primary" disabled={status.isPending} onClick={() => status.mutate('ON_SALE')}>
                  Put on sale
                </Button>
              ) : null}
              {manage && p.status === 'ON_SALE' ? (
                <Button disabled={status.isPending} onClick={() => status.mutate('OFF_SALE')}>
                  Take off sale
                </Button>
              ) : null}
            </div>
          }
        >
          <div className="grid gap-4 px-5 py-4 text-sm md:grid-cols-3">
            <div>
              <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Price</p>
              <p className="tabular mt-1 text-2xl font-bold text-ink-900">{p.price_cents === null ? '—' : dollars(p.price_cents)}</p>
              <p className="text-xs text-ink-400">{p.prices.map((x) => `${x.channel ?? 'all channels'} ${dollars(x.price_cents)}`).join(' · ') || 'not priced'}</p>
              {p.face_total_cents !== null ? <p className="text-xs text-ink-600">Face value of one set: {dollars(p.face_total_cents)}</p> : null}
            </div>
            <div>
              <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Holders</p>
              <p className="tabular mt-1 text-2xl font-bold text-ink-900">{p.holders.active}</p>
              <p className="text-xs text-ink-400">{p.holders.pending} checking out</p>
            </div>
            {renewable ? (
              <div>
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Renewals</p>
                <p className="tabular mt-1 text-2xl font-bold text-ink-900">{p.renewals.offered}</p>
                <p className="text-xs text-ink-400">{p.renewals.expires_at ? `offered until ${formatWhen(p.renewals.expires_at)}` : p.previous ? 'none offered yet' : 'set the package this one renews'}</p>
              </div>
            ) : null}
            {p.package_kind === 'MEMBERSHIP_PASS' ? (
              <div>
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Pass terms</p>
                <p className="mt-1 text-ink-900">
                  {p.pass_admissions_per_performance} per {dateWords().one}{p.pass_total_redemptions ? `, ${p.pass_total_redemptions} in all` : ''}
                </p>
                <p className="text-xs text-ink-400">
                  {p.pass_valid_from ? formatWhen(p.pass_valid_from) : 'from purchase'} → {p.pass_valid_to ? formatWhen(p.pass_valid_to) : 'one year'}
                </p>
              </div>
            ) : null}
          </div>
          {p.description ? <p className="px-5 pb-4 text-sm text-ink-600">{p.description}</p> : null}
        </Panel>
      )}

      <ComponentsPanel product={p} manage={manage && p.holders.active + p.holders.pending === 0} onError={setError} onChanged={refresh} />
      <AreasPanel product={p} manage={manage} onError={setError} onChanged={refresh} />
      <PricesPanel product={p} manage={manage} onError={setError} onChanged={refresh} />
      <InstallmentTermsPanel packageId={p.id} isPass={p.package_kind === 'MEMBERSHIP_PASS'} manage={manage} onError={setError} />

      {renewable && manage && p.previous ? (
        <Panel title="Renewals" description={`Subscribers of ${p.previous.name} get their seats held on every ${dateWords().one} here until the deadline, and an email inviting them to renew. After the deadline, Lapse releases what nobody renewed (the hourly job does this too).`}>
          <RenewalActions offering={offer.isPending} lapsing={lapse.isPending} onOffer={(at) => offer.mutate(at)} onLapse={() => lapse.mutate()} disabled={p.status !== 'ON_SALE'} />
        </Panel>
      ) : null}

      <Panel title="Holders" description="Everyone who holds this package, with their seats and the state of the subscription.">
        <Table headers={['Customer', 'Order', 'Seats', 'Tickets', 'Status', 'Bought']}>
          {detail.data.instances.length === 0 ? (
            <EmptyRow columns={6}>Nobody yet.</EmptyRow>
          ) : (
            detail.data.instances.map((i) => (
              <tr key={i.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm">
                  <div className="text-ink-900">{i.customer_name ?? '—'}</div>
                  <div className="text-xs text-ink-400">{i.customer_email ?? ''}</div>
                </td>
                <td className="px-5 py-3 font-mono text-sm">
                  <Link to={`/orders/${i.order_id}`} className="hover:underline">
                    {i.order_number}
                  </Link>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{i.seats ?? '—'}</td>
                <td className="tabular px-5 py-3 text-sm">{i.tickets}</td>
                <td className="px-5 py-3">
                  <Badge tone={i.status === 'ACTIVE' ? 'ok' : i.status === 'RENEWED' ? 'neutral' : 'warn'}>{humanize(i.status)}</Badge>
                  {i.subscription_status ? <div className="text-xs text-ink-400">seats {humanize(i.subscription_status).toLowerCase()}{i.renewal_offer_expires_at ? ` until ${formatWhen(i.renewal_offer_expires_at)}` : ''}</div> : null}
                </td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(i.created_at)}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function RenewalActions({ offering, lapsing, onOffer, onLapse, disabled }: { offering: boolean; lapsing: boolean; onOffer: (expiresAt: string) => void; onLapse: () => void; disabled: boolean }) {
  const [deadline, setDeadline] = useState('');
  return (
    <div className="flex flex-wrap items-end gap-3 px-5 py-4">
      <Field label="Renewal deadline" htmlFor="rn-deadline">
        <input id="rn-deadline" type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} className={INPUT} />
      </Field>
      <Button variant="primary" disabled={offering || !deadline || disabled} onClick={() => onOffer(toIso(deadline)!)}>
        Offer renewals
      </Button>
      <Button variant="danger" disabled={lapsing} onClick={() => window.confirm('Release every seat still held for renewal and end those subscriptions?') && onLapse()}>
        Lapse unrenewed
      </Button>
      {disabled ? <span className="text-xs text-warn">Put the package on sale first.</span> : null}
    </div>
  );
}

function ComponentsPanel({ product, manage, onError, onChanged }: { product: PackageRow; manage: boolean; onError: (m: string) => void; onChanged: () => void }) {
  const isPass = product.package_kind === 'MEMBERSHIP_PASS';
  // In a choose-your-own package the components are what the customer picks
  // between, so a new one starts optional. Marking them all required leaves
  // nothing to choose, which is the state that made one unbuyable (D-107).
  const chooseYourOwn = product.package_kind === 'CHOOSE_YOUR_OWN';
  const events = useQuery({ queryKey: ['events', 'all'], queryFn: () => eventEndpoints.list() });
  /*
   * Each row picks its event first and its date from that event's list, so a
   * row needs the event even when the component names only its date — which
   * is what a season or a series writes. Reading `event_id` alone showed those
   * games as unchosen when the package was opened for editing (D-384).
   */
  const [rows, setRows] = useState(product.components.map((c) => ({ event_id: c.resolved_event_id ?? c.event_id ?? '', performance_id: c.performance_id ?? '', ticket_type_id: c.ticket_type_id ?? '', quantity: String(c.quantity), required: c.required === 1, fixed_allocation_cents: c.fixed_allocation_cents === null ? '' : (c.fixed_allocation_cents / 100).toFixed(2) })));
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setRows(product.components.map((c) => ({ event_id: c.resolved_event_id ?? c.event_id ?? '', performance_id: c.performance_id ?? '', ticket_type_id: c.ticket_type_id ?? '', quantity: String(c.quantity), required: c.required === 1, fixed_allocation_cents: c.fixed_allocation_cents === null ? '' : (c.fixed_allocation_cents / 100).toFixed(2) })));
    setDirty(false);
  }, [product]);
  const save = useMutation({
    mutationFn: () => packageEndpoints.setComponents(product.id, { components: rows.map((r) => ({ event_id: r.event_id || null, performance_id: isPass ? null : r.performance_id || null, ticket_type_id: r.ticket_type_id || null, quantity: Number(r.quantity) || 1, required: r.required, fixed_allocation_cents: r.fixed_allocation_cents ? toCents(r.fixed_allocation_cents) : null })) }),
    onSuccess: () => {
      setDirty(false);
      onChanged();
    },
    onError: (cause: ApiError) => onError(cause.message),
  });
  const update = (index: number, patch: Partial<(typeof rows)[number]>) => {
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  return (
    <Panel
      title="Components"
      description={isPass ? 'The events this pass admits to, and the ticket type a redemption issues.' : `Each ${dateWords().one} in the package with its ticket type and how many seats per set.${product.package_kind === 'CHOOSE_YOUR_OWN' ? ` Untick Required for the ${dateWords().many} the customer chooses between.` : ''}${manage ? '' : ' Locked while anyone holds the package.'}`}
      actions={
        manage ? (
          <div className="flex gap-2">
            <Button onClick={() => { setRows([...rows, { event_id: '', performance_id: '', ticket_type_id: '', quantity: '1', required: !chooseYourOwn, fixed_allocation_cents: '' }]); setDirty(true); }}>Add</Button>
            <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              Save components
            </Button>
          </div>
        ) : null
      }
    >
      {chooseYourOwn && rows.filter((r) => !r.required).length < (product.min_selections ?? 1) ? (
        <p className="mx-5 mt-4 rounded border border-warn/40 bg-warn-soft px-4 py-2 text-sm text-ink-900">
          A customer picks {product.min_selections ?? 1} of these, but only {rows.filter((r) => !r.required).length} {rows.filter((r) => !r.required).length === 1 ? 'is' : 'are'} optional. Clear <strong>Required</strong> on the ones they choose between — otherwise there is nothing to choose and the package cannot go on sale.
        </p>
      ) : null}
      {manage ? <SeasonComponents product={product} dirty={dirty} onError={onError} onChanged={onChanged} /> : null}
      <Table headers={[isPass ? 'Event' : `Event / ${dateWords().one}`, 'Ticket type', isPass ? '' : 'Seats per set', isPass ? '' : 'Required', product.allocation_method === 'FIXED_AMOUNTS' ? 'Fixed amount' : '', '']}>
        {rows.length === 0 ? (
          <EmptyRow columns={6}>No components yet.</EmptyRow>
        ) : (
          rows.map((row, index) => (
            <ComponentRow key={index} row={row} isPass={isPass} manage={manage} events={events.data?.events ?? []} fixed={product.allocation_method === 'FIXED_AMOUNTS'} onChange={(patch) => update(index, patch)} onRemove={() => { setRows(rows.filter((_, i) => i !== index)); setDirty(true); }} label={product.components[index] ? `${product.components[index]!.event_title ?? ''}${product.components[index]!.performance_starts_at ? ` · ${formatWhen(product.components[index]!.performance_starts_at)}` : ''}` : ''} />
          ))
        )}
      </Table>
    </Panel>
  );
}

function ComponentRow({ row, isPass, manage, events, fixed, onChange, onRemove, label }: { row: { event_id: string; performance_id: string; ticket_type_id: string; quantity: string; required: boolean; fixed_allocation_cents: string }; isPass: boolean; manage: boolean; events: { id: string; title: string }[]; fixed: boolean; onChange: (patch: Partial<{ event_id: string; performance_id: string; ticket_type_id: string; quantity: string; required: boolean; fixed_allocation_cents: string }>) => void; onRemove: () => void; label: string }) {
  const performances = useQuery({ queryKey: ['performances', row.event_id], queryFn: () => eventEndpoints.performances(row.event_id), enabled: Boolean(row.event_id) && !isPass });
  const types = useQuery({ queryKey: ['ticket-types', row.event_id], queryFn: () => eventEndpoints.ticketTypes(row.event_id), enabled: Boolean(row.event_id) });
  if (!manage) {
    return (
      <tr className="border-b border-line last:border-0">
        <td className="px-5 py-3 text-sm text-ink-900">{label || row.event_id}</td>
        <td className="px-5 py-3 text-sm text-ink-600">{(types.data?.ticket_types ?? []).find((t: TicketTypeRow) => t.id === row.ticket_type_id)?.name ?? row.ticket_type_id}</td>
        <td className="tabular px-5 py-3 text-sm">{isPass ? '' : row.quantity}</td>
        <td className="px-5 py-3 text-sm">{isPass ? '' : row.required ? 'yes' : 'optional'}</td>
        <td className="tabular px-5 py-3 text-sm">{fixed && row.fixed_allocation_cents ? `$${row.fixed_allocation_cents}` : ''}</td>
        <td />
      </tr>
    );
  }
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-5 py-2">
        <div className="flex flex-wrap gap-1">
          <select value={row.event_id} onChange={(e) => onChange({ event_id: e.target.value, performance_id: '', ticket_type_id: '' })} className={INPUT} aria-label="Event">
            <option value="">Event…</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title}
              </option>
            ))}
          </select>
          {!isPass ? (
            <select value={row.performance_id} onChange={(e) => onChange({ performance_id: e.target.value })} className={INPUT} aria-label={`${dateWords().One}`} disabled={!row.event_id}>
              <option value="">{dateWords().One}…</option>
              {(performances.data?.performances ?? []).map((p: PerformanceRow) => (
                <option key={p.id} value={p.id}>
                  {formatWhen(p.starts_at)} · {p.status.toLowerCase().replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </td>
      <td className="px-5 py-2">
        <select value={row.ticket_type_id} onChange={(e) => onChange({ ticket_type_id: e.target.value })} className={INPUT} aria-label="Ticket type" disabled={!row.event_id}>
          <option value="">Type…</option>
          {(types.data?.ticket_types ?? []).map((t: TicketTypeRow) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </td>
      <td className="px-5 py-2">{isPass ? null : <input type="number" min={1} max={50} value={row.quantity} onChange={(e) => onChange({ quantity: e.target.value })} className={`${INPUT} w-16`} aria-label="Seats per set" />}</td>
      <td className="px-5 py-2">{isPass ? null : <input type="checkbox" checked={row.required} onChange={(e) => onChange({ required: e.target.checked })} aria-label="Required" />}</td>
      <td className="px-5 py-2">{fixed ? <input type="number" step="0.01" min={0} value={row.fixed_allocation_cents} onChange={(e) => onChange({ fixed_allocation_cents: e.target.value })} className={`${INPUT} w-24`} aria-label="Fixed amount" /> : null}</td>
      <td className="px-5 py-2 text-right">
        <Button onClick={onRemove}>Remove</Button>
      </td>
    </tr>
  );
}

/**
 * Where the package may be seated (D-384): Box Seats, general admission,
 * Stingerz Landing — the price zones its games are sold in, by code, so one
 * choice covers every game. None ticked is anywhere. The hold enforces it at
 * the storefront and the window; the maps gray out the rest. A membership
 * pass takes the same setting (D-385): each redemption seats the holder only
 * there, and the pass says "Good for … only" to its holder and the window.
 */
function AreasPanel({ product, manage, onError, onChanged }: { product: PackageRow; manage: boolean; onError: (m: string) => void; onChanged: () => void }) {
  const areas = useQuery({ queryKey: ['packages', product.id, 'areas'], queryFn: () => packageEndpoints.areas(product.id) });
  const [chosen, setChosen] = useState<string[]>(product.price_zone_codes);
  useEffect(() => setChosen(product.price_zone_codes), [product]);
  const save = useMutation({ mutationFn: () => packageEndpoints.setAreas(product.id, chosen), onSuccess: onChanged, onError: (cause: ApiError) => onError(cause.message) });
  const choices = areas.data?.choices ?? [];
  const dirty = [...chosen].sort().join(',') !== [...product.price_zone_codes].sort().join(',');
  // No reserved seats in any ticked area: general admission only.
  const gaOnly = chosen.length > 0 && choices.length > 0 && chosen.every((code) => choices.find((c) => c.code === code)?.reserved === 0);
  const isPass = product.package_kind === 'MEMBERSHIP_PASS';
  const chosenNames = choices.filter((c) => chosen.includes(c.code)).map((c) => c.name).join(', ') || chosen.join(', ');
  return (
    <Panel
      title="Seating areas"
      description={
        isPass
          ? `Where this pass may seat its holder when it is redeemed for a ${dateWords().one}, at the account page or the window. Leave all unticked for anywhere. The pass shows "Good for … only"; tickets already redeemed keep their seats.`
          : `Where this package may be seated, on every ${dateWords().one} in it. Leave all unticked to sell it anywhere. Seats and places outside the ticked areas are grayed out on the map and refused when held.`
      }
      actions={manage ? <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>Save areas</Button> : null}
    >
      <div className="space-y-2 px-5 py-4 text-sm">
        {areas.isPending ? <p className="text-ink-400">Loading…</p> : null}
        {!areas.isPending && choices.length === 0 ? <p className="text-ink-400">Add the {dateWords().many} first; the areas are the price zones they are sold in.</p> : null}
        <div className="flex flex-wrap gap-4">
          {choices.map((choice) => (
            <label key={choice.code} className="flex items-center gap-2 text-ink-900">
              <input
                type="checkbox"
                disabled={!manage}
                checked={chosen.includes(choice.code)}
                onChange={(e) => setChosen(e.target.checked ? [...chosen, choice.code] : chosen.filter((code) => code !== choice.code))}
              />
              {choice.name} <span className="font-mono text-xs text-ink-400">{choice.code}</span>
              {choice.events < new Set(product.components.map((k) => k.resolved_event_id)).size ? <span className="text-xs text-warn">only {choice.events} of the events</span> : null}
            </label>
          ))}
        </div>
        <p className="text-xs text-ink-500">
          {isPass ? (chosen.length === 0 ? 'Redeems anywhere.' : `Good for ${chosenNames} only.`) : chosen.length === 0 ? 'Sold anywhere.' : `Sold only in ${chosenNames}.`}
          {isPass && gaOnly ? ` Each redemption takes places in general admission.` : ''}
          {!isPass && gaOnly && (product.package_kind === 'SEASON_TICKET' || product.same_seat_every_performance === 1) ? ` General admission has no seat to keep: each ${dateWords().one} takes a place in the area, and there is no seat to renew.` : ''}
        </p>
      </div>
    </Panel>
  );
}

function PricesPanel({ product, manage, onError, onChanged }: { product: PackageRow; manage: boolean; onError: (m: string) => void; onChanged: () => void }) {
  const [prices, setPrices] = useState<{ channel: string; price: string }[]>(product.prices.length ? product.prices.map((p) => ({ channel: p.channel ?? '', price: (p.price_cents / 100).toFixed(2) })) : [{ channel: '', price: '' }]);
  useEffect(() => {
    setPrices(product.prices.length ? product.prices.map((p) => ({ channel: p.channel ?? '', price: (p.price_cents / 100).toFixed(2) })) : [{ channel: '', price: '' }]);
  }, [product]);
  const save = useMutation({
    mutationFn: () => packageEndpoints.setPrices(product.id, { prices: prices.filter((p) => p.price !== '').map((p) => ({ channel: p.channel || null, price_cents: toCents(p.price) ?? 0 })) }),
    onSuccess: onChanged,
    onError: (cause: ApiError) => onError(cause.message),
  });
  return (
    <Panel title="Prices" description="One price per channel; a price with no channel applies everywhere a channel-specific one does not. Saving supersedes the earlier prices; orders already placed keep theirs." actions={manage ? <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>Save prices</Button> : null}>
      <div className="space-y-2 px-5 py-4">
        {prices.map((p, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <select value={p.channel} onChange={(e) => setPrices(prices.map((x, j) => (j === i ? { ...x, channel: e.target.value } : x)))} className={INPUT} disabled={!manage} aria-label="Channel">
              <option value="">All channels</option>
              <option value="WEB">Web</option>
              <option value="BOX_OFFICE">Box office</option>
              <option value="PHONE">Phone</option>
              <option value="ADMIN">Admin</option>
            </select>
            <input type="number" step="0.01" min={0} value={p.price} onChange={(e) => setPrices(prices.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} className={`${INPUT} w-28`} disabled={!manage} aria-label="Price" placeholder="0.00" />
            {manage && prices.length > 1 ? <Button onClick={() => setPrices(prices.filter((_, j) => j !== i))}>Remove</Button> : null}
          </div>
        ))}
        {manage ? <Button onClick={() => setPrices([...prices, { channel: 'BOX_OFFICE', price: '' }])}>Add a channel price</Button> : null}
      </div>
    </Panel>
  );
}

/**
 * Every game of a season as components, in one step (D-371). A season ticket
 * for seventy home games is seventy components; this adds them from the
 * season, or the Fridays, the Fireworks nights or a date range, using each
 * game's own ticket type with the code chosen.
 */
function SeasonComponents({ product, dirty, onError, onChanged }: { product: PackageRow; dirty: boolean; onError: (m: string) => void; onChanged: () => void }) {
  const seasons = useQuery({ queryKey: ['collections', 'seasons'], queryFn: () => eventEndpoints.seasons() });
  const [open, setOpen] = useState(false);
  const [seasonId, setSeasonId] = useState(product.season_id ?? '');
  const games = useQuery({ queryKey: ['season-games', seasonId], queryFn: () => seasonEndpoints.games(seasonId), enabled: open && Boolean(seasonId) });
  const [code, setCode] = useState('');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [mode, setMode] = useState<'APPEND' | 'REPLACE'>('APPEND');
  const [plan, setPlan] = useState<SeasonComponentsPlan | null>(null);
  const input = (dryRun: boolean): SeasonComponentsInput => ({
    season_id: seasonId,
    selection: { ...(weekdays.length ? { weekdays } : {}), ...(categoryId ? { category_ids: [categoryId] } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}) },
    ticket_type_code: code,
    quantity: Number(quantity) || 1,
    mode,
    dry_run: dryRun,
  });
  const preview = useMutation({ mutationFn: () => seasonEndpoints.packageComponents(product.id, input(true)), onSuccess: setPlan, onError: (cause: ApiError) => onError(cause.message) });
  const apply = useMutation({
    mutationFn: () => seasonEndpoints.packageComponents(product.id, input(false)),
    onSuccess: () => {
      setPlan(null);
      setOpen(false);
      onChanged();
    },
    onError: (cause: ApiError) => onError(cause.message),
  });
  const categories = [...new Map((games.data?.games ?? []).flatMap((g) => g.categories).map((c) => [c.id, c.name])).entries()];
  const reset = () => setPlan(null);

  if (!open) {
    return (
      <div className="border-b border-line px-5 py-3">
        <Button onClick={() => setOpen(true)}>Add every game in a season…</Button>
      </div>
    );
  }
  return (
    <div className="space-y-3 border-b border-line px-5 py-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Season" htmlFor="sc-season">
          <select
            id="sc-season"
            value={seasonId}
            onChange={(e) => {
              setSeasonId(e.target.value);
              setCode('');
              reset();
            }}
            className={INPUT}
          >
            <option value="">Choose…</option>
            {(seasons.data?.seasons ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Ticket type" htmlFor="sc-type" hint="Each game’s own type with this code">
          <select
            id="sc-type"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              reset();
            }}
            className={INPUT}
          >
            <option value="">Choose…</option>
            {(games.data?.ticket_types ?? []).map((type) => (
              <option key={type.code} value={type.code}>
                {type.name} ({type.code}, {type.games} {type.games === 1 ? 'game' : 'games'})
              </option>
            ))}
          </select>
        </Field>
        {product.package_kind === 'MEMBERSHIP_PASS' ? null : (
          <Field label="Seats per set" htmlFor="sc-qty">
            <input
              id="sc-qty"
              value={quantity}
              onChange={(e) => {
                setQuantity(e.target.value);
                reset();
              }}
              inputMode="numeric"
              className={`w-16 ${INPUT}`}
            />
          </Field>
        )}
        <Field label="Existing components" htmlFor="sc-mode">
          <select
            id="sc-mode"
            value={mode}
            onChange={(e) => {
              setMode(e.target.value as 'APPEND' | 'REPLACE');
              reset();
            }}
            className={INPUT}
          >
            <option value="APPEND">Keep them, add these</option>
            <option value="REPLACE">Replace them with these</option>
          </select>
        </Field>
      </div>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Only</span>
        {WEEKDAYS.map((day, index) => (
          <label key={day} className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={weekdays.includes(index)}
              onChange={(e) => {
                setWeekdays(e.target.checked ? [...weekdays, index] : weekdays.filter((d) => d !== index));
                reset();
              }}
            />
            {day}
          </label>
        ))}
        <select
          aria-label="Category"
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            reset();
          }}
          className={INPUT}
        >
          <option value="">Any category</option>
          {categories.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <input
          type="date"
          aria-label="From"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            reset();
          }}
          className={INPUT}
        />
        <input
          type="date"
          aria-label="To"
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            reset();
          }}
          className={INPUT}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" disabled={!seasonId || !code || preview.isPending} onClick={() => preview.mutate()}>
          Preview
        </Button>
        <Button disabled={!plan || (plan.adding.length === 0 && mode === 'APPEND') || apply.isPending || dirty} onClick={() => apply.mutate()}>
          {plan ? `Save ${plan.total} components` : 'Save'}
        </Button>
        <Button
          onClick={() => {
            setOpen(false);
            reset();
          }}
        >
          Cancel
        </Button>
        {dirty ? <span className="text-xs text-warn">Save or undo the changes below first.</span> : null}
      </div>
      {plan ? (
        <div className="text-sm text-ink-600">
          <p>
            Adds {plan.adding.length} {plan.adding.length === 1 ? 'game' : 'games'}
            {mode === 'APPEND' ? `, keeps ${plan.kept}` : ', replacing what is there'}: {plan.total} in all.
          </p>
          {plan.adding.length > 0 ? <p className="text-xs text-ink-400">{plan.adding.map((game) => `${game.starts_at ? formatWhen(game.starts_at) : ''} ${game.title}`.trim()).join(' · ')}</p> : null}
          {plan.skipped.length > 0 ? (
            <ul className="mt-1 text-xs text-warn">
              {plan.skipped.map((game) => (
                <li key={game.event_id}>
                  {game.title}: {game.reason}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
